import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppState } from '@/context/appState';
import { GetAllProductsData, ProductAPI } from '@/helpers/api/ProductAPI';
import {
  ErrorNotification,
  ReloadNotification,
} from '@/components/toast/toastMessages';
import { Product } from '@/context/types';
import { ShopifyProductsResponse } from '@/context/shopifyTypes';
import { parseJSONMeta } from '@/helpers/misc';
export type ILine = Product & { quantity?: number };
export type BoxLine = ILine & { capacity?: number };

export interface ProductHierarchy {
  standardSections: Record<string, Product[]>; // Keys: "Apparel", "Drinkware", "Misc/Other"
  mixAndMatchBoxes: Record<string, Product[]>; // Keys: "mix:summer", "mix:fitness"
  boxes: { [key: string]: Record<string, BoxLine> }; // Ascending Capacity Order
}

export function buildProductHierarchy(products: Product[]): ProductHierarchy {
  const groupableCandidates: Product[] = [];
  const initialNonGroupable: Product[] = [];
  const initialBoxes: BoxLine[] = [];

  // STEP 1: Initial Split
  products.forEach((product) => {
    // Normalize tags to lowercase for safe comparison
    const normalizedTags = product.tags.map((t) => t.toLowerCase().trim());
    if (normalizedTags.includes('groupable')) {
      groupableCandidates.push(product);
    } else if (product.productType.includes('Praline Bundle')) {
      const capacity = product.tags
        .find((t) => t.includes('QUANTITY'))
        ?.replace('QUANTITY:', '');

      if (!capacity) return;

      initialBoxes.push({ ...product, capacity: parseInt(capacity!) });
    } else {
      initialNonGroupable.push(product);
    }
  });

  // STEP 2 & 3: Evaluate "mix:" tags and valid Mix-and-Match Boxes
  const assignedIds = new Set<string>();

  const boxMap: { [key: string]: Record<string, BoxLine> } =
    initialBoxes.reduce(
      (dict: { [key: string]: Record<string, BoxLine> }, box) => {
        const boxMixVariant = 'mix:' + box.variantTitle!.toLowerCase().trim();
        if (!dict[boxMixVariant]) {
          dict[boxMixVariant] = { [box.id]: box };
        } else {
          dict[boxMixVariant][box.id] = box;
        }

        assignedIds.add(box.id);
        return dict;
      },
      {},
    );

  const rawMixMap: Record<string, Product[]> = {};

  groupableCandidates.forEach((product) => {
    // Isolate ONLY tags starting with "mix:" (solves System Tag edge case)
    const mixTags = product.tags
      .map((t) => t.toLowerCase().trim())
      .filter((t) => t.startsWith('mix:'));

    mixTags.forEach((tag) => {
      if (!rawMixMap[tag]) rawMixMap[tag] = [];
      rawMixMap[tag].push(product);
    });
  });

  const validMixBoxes: Record<string, Product[]> = {};

  // Verify which mix tags have 2 or more products (Rule 3)
  Object.entries(rawMixMap).forEach(([tag, groupedProducts]) => {
    if (groupedProducts.length >= 2) {
      validMixBoxes[tag] = groupedProducts;
      // Mark these products as successfully grouped
      // groupedProducts.forEach((p) => assignedIds.add(p.id));
    }
  });

  // STEP 4: Handle "Orphan" and "Naked" Edge Cases
  // Any candidate that didn't make it into at least one valid box gets demoted.
  const orphans = groupableCandidates.filter(
    (product) => !assignedIds.has(product.id),
  );

  // Combine native non-groupables with demoted orphans
  const finalStandardProducts = [...initialNonGroupable, ...orphans];

  // STEP 5: Build Standard Sections (Rules 1a & 1b)
  const standardSections: Record<string, Product[]> = {
    'Misc/Other': [],
  };

  // Count product types to determine if they go to their own section or Misc
  const typeCounts = finalStandardProducts.reduce(
    (acc, product) => {
      const type = product.productType || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  // Distribute based on count
  finalStandardProducts.forEach((product) => {
    const type = product.productType || 'Unknown';

    if (typeCounts[type] > 1) {
      if (!standardSections[type]) {
        standardSections[type] = [];
      }
      standardSections[type].push(product);
    } else {
      standardSections['Misc/Other'].push(product);
    }
  });

  // Clean up empty Misc/Other section if it's unused
  if (standardSections['Misc/Other'].length === 0) {
    delete standardSections['Misc/Other'];
  }

  // RETURN FINAL PAYLOAD
  return {
    standardSections,
    mixAndMatchBoxes: validMixBoxes,
    boxes: boxMap,
  };
}

export default function useCatalog(numeric_id: string = '') {
  const { products } = useAppState();
  const dispatch = useAppDispatch();

  // Ref to prevent React 18 Strict Mode double-fetching
  const hasFetched = useRef(false);

  const getProducts = useCallback(async () => {
    // Prevent duplicate network requests if already loading or ready
    if (products.loading || products.ready) return;

    dispatch({ type: 'LOADING_PRODUCTS' });

    try {
      const res = await ProductAPI.post();

      // 1. Bulletproof GraphQL Error Catching
      if (res.errors && res.errors.length > 0) {
        dispatch({ type: 'PRODUCT_FETCH_ERROR', err: res.errors[0] as Error });
        throw new Error(res.errors[0].message);
      }

      // 2. Data Structure Transformation
      const flattenProducts = (data: ShopifyProductsResponse): Product[] => {
        return data.products.edges.flatMap(({ node: product }) => {
          let numVariants = product.variants.edges.length;
          return product.variants.edges.map(({ node: variant }) => {
            const rawNutrition = product?.nutrition_facts?.reference;
            const capacity = product.tags
              .find((t) => t.includes('QUANTITY'))
              ?.replace('QUANTITY:', '');
            return {
              // Parent Product Info
              parentId: product.id,
              numericId: variant.id?.split('/').pop()!,
              handle: product.handle,
              title: capacity
                ? product.title
                : numVariants > 1
                  ? variant?.title
                  : product.title,
              descriptionHtml: product.descriptionHtml,
              tags: product.tags,
              collections: product.collections.edges.map((e) => e.node),
              allergens: product?.allergens?.references?.nodes?.map(
                (node: { field: { value: string } }) => node?.field?.value,
              ),
              savings: product.savings,
              productType: product.productType,
              // Variant Specific Info (The "Flat" Product)
              id: variant.id,
              sku: variant.sku,
              variantTitle: variant.title,
              // Combined title for display (e.g., "Cool Shirt - Blue / Large")
              fullTitle:
                variant.title === 'Default Title'
                  ? product.title
                  : `${product.title} - ${variant.title}`,
              price: parseFloat(variant.price.amount),
              compareAtPrice: variant.compareAtPrice?.amount,
              available: variant.availableForSale,
              options: variant.selectedOptions,

              // Map Nutrition Facts (Only if they exist)
              nutritionFacts: rawNutrition
                ? {
                    calories: parseFloat(rawNutrition.calories?.value ?? '0'),
                    protein: parseJSONMeta(rawNutrition.protein),
                    totalSugars: parseJSONMeta(rawNutrition.totalSugars),
                    dietaryFiber: parseJSONMeta(rawNutrition.dietaryFiber),
                    totalCarbohydrates: parseJSONMeta(
                      rawNutrition.totalCarbohydrates,
                    ),
                    cholesterol: parseJSONMeta(rawNutrition.cholesterol),
                    transFat: parseJSONMeta(rawNutrition.transFat),
                    totalFat: parseJSONMeta(rawNutrition.totalFat),
                    saturatedFat: parseJSONMeta(rawNutrition.saturatedFat),
                    servingSize: {
                      amount: parseFloat(
                        rawNutrition.servingSize?.reference?.amount?.value ??
                          '0',
                      ),
                      weight: parseJSONMeta(
                        rawNutrition.servingSize?.reference?.weight,
                      ),
                    },
                  }
                : null,

              // Use variant image if available, fallback to product featured image
              img: {
                alt: variant.image?.altText || product.featuredImage?.altText,
                url: variant.image?.url || product.featuredImage?.url,
              },
            };
          });
        });
      };
      const flatProducts = flattenProducts(res.data as any);

      // Usage:
      // const flatProductsList = flattenProducts(response.data);

      // 3. Dispatch the cleanly mapped data
      dispatch({
        type: 'PRODUCTS_READY',
        hierarchy: buildProductHierarchy(flatProducts),
      });
    } catch (err: any) {
      console.error('Storefront API Error:', err);
      dispatch({ type: 'PRODUCT_FETCH_ERROR', err });
      // Use the Reload Notification for critical catalog failures
      ReloadNotification(
        'Unable to load the store menu. Please check your connection and tap to refresh.',
        getProducts,
      );
    }
  }, [dispatch, products.loading, products.ready]);

  useEffect(() => {
    // Only run once per component lifecycle, protecting against Strict Mode
    if (!hasFetched.current) {
      hasFetched.current = true;
      getProducts();
    }
  }, [getProducts]);
  const queryGid = 'gid://shopify/ProductVariant/' + numeric_id;
  const queryItem = useMemo(
    () =>
      products.ready
        ? Object.values(products.data?.standardSections!).reduce<
            Product | undefined
          >((p, list) => {
            if (!p) {
              p = list.find(({ id: queriedId }) => {
                return queriedId === queryGid;
              });
            }
            return p;
          }, undefined)
        : undefined,
    [numeric_id, products.ready],
  );
  return {
    isReady: products.ready,
    isLoading: products.loading,
    error: products.error, // ADD THIS: Expose the error state to the UI
    hierarchy: products.data,
    refreshCatalog: getProducts, // You already have this, which is perfect for our Retry button!
    item: queryItem,
  };
}
